import axios from 'axios';
import { captureGaIdentity } from './utils/analytics';

class AuthService {
  constructor() {
    // Normalize API URL - remove trailing /api if present to avoid double /api/api/
    let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    if (apiUrl.endsWith('/api')) {
      apiUrl = apiUrl.slice(0, -4);
    }
    this.baseURL = apiUrl;
    console.log('AuthService initialized with baseURL:', this.baseURL);
    
    this.currentUser = null;
    this.paymentConfig = null;
    this.storageKey = process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt_tokens';
    this.refreshThreshold = parseInt(process.env.REACT_APP_JWT_REFRESH_THRESHOLD) || 300000; // 5 minutes
    this.requireLoginForComparison = String(process.env.REACT_APP_REQUIRE_LOGIN_FOR_COMPARISON ?? 'true').toLowerCase() === 'true';
    console.log('AuthService requireLoginForComparison:', this.requireLoginForComparison);
    
    // Throttle refresh attempts
    this.refreshCooldownMs = parseInt(process.env.REACT_APP_JWT_REFRESH_COOLDOWN_MS) || 5000; // 5s default
    this.lastRefreshAttemptAt = 0;
    this.refreshInFlight = null;
    
    // Configure axios defaults for JWT authentication
    this.setupAxiosInterceptors();
    
    // Try to restore tokens from localStorage on initialization
    this.restoreTokens();
  }

  // Check if a token is expired
  isTokenExpired(token) {
    if (!token) return true;
    const tokenData = this.parseJwt(token);
    if (!tokenData || !tokenData.exp) return true;
    const now = Date.now() / 1000;
    return tokenData.exp < now;
  }

  // Check if an endpoint is public (doesn't require authentication)
  isPublicEndpoint(url) {
    if (!url) return false;
    const publicPatterns = [
      '/institutions/',
      '/institutions-list/',
      '/api/institutions/',
      '/api/payment-config/',
      '/api/token/',
      '/api/token/refresh/',
      '/api/register/',
      '/api/password-reset/',
      '/institution/slug/',
      '/map-viewport/',
    ];
    return publicPatterns.some(pattern => url.includes(pattern));
  }

  setupAxiosInterceptors() {
    // Request interceptor to add JWT token
    axios.interceptors.request.use(
      (config) => {
        // Skip auth header for refresh token requests
        if (config.skipAuthHeader) {
          return config;
        }
        
        // If login isn't required, don't attach auth headers
        if (!this.requireLoginForComparison) return config;
        
        const tokens = this.getTokens();
        if (tokens && tokens.access) {
          // Check if token is expired
          if (this.isTokenExpired(tokens.access)) {
            // For public endpoints, don't send expired token
            if (this.isPublicEndpoint(config.url)) {
              // Remove Authorization header if present
              delete config.headers.Authorization;
              return config;
            }
            // For protected endpoints, try to refresh first
            // But don't block the request - let the response interceptor handle it
          }
          config.headers.Authorization = `Bearer ${tokens.access}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Check if this is a token expiration error
          const isTokenExpiredError = error.response?.data?.code === 'token_not_valid' ||
            error.response?.data?.messages?.some(msg => msg.message === 'Token is expired') ||
            error.response?.data?.detail === 'Token is expired';

          // If login isn't required, don't try to refresh or redirect
          if (!this.requireLoginForComparison) {
            // Clear expired tokens but don't redirect
            if (isTokenExpiredError) {
              this.clearTokens();
              this.currentUser = null;
            }
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const refreshed = await this.refreshToken();
            if (refreshed) {
              const tokens = this.getTokens();
              originalRequest.headers.Authorization = `Bearer ${tokens.access}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed - token may be expired
            // Clear expired tokens
            this.clearTokens();
            this.currentUser = null;
            
            // For public endpoints, allow the request to proceed without auth
            if (this.isPublicEndpoint(originalRequest.url)) {
              // Remove Authorization header and retry without auth
              delete originalRequest.headers.Authorization;
              return axios(originalRequest);
            }
            
            // For protected endpoints, only redirect if we're on a protected page
            // Don't redirect if we're on a public page (like home or institutions list)
            const currentPath = window.location.pathname;
            const publicPages = ['/', '/institutions', '/map', '/about'];
            const isPublicPage = publicPages.some(page => currentPath === page || currentPath.startsWith(page + '/'));
            
            if (!isPublicPage && this.requireLoginForComparison) {
              window.location.href = '/login';
            }
            
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  getTokens() {
    try {
      const tokens = localStorage.getItem(this.storageKey);
      return tokens ? JSON.parse(tokens) : null;
    } catch (error) {
      console.error('Error getting tokens from localStorage:', error);
      return null;
    }
  }

  setTokens(tokens) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(tokens));
    } catch (error) {
      console.error('Error setting tokens in localStorage:', error);
    }
  }

  clearTokens() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Error clearing tokens from localStorage:', error);
    }
  }

  restoreTokens() {
    if (!this.requireLoginForComparison) return;
    const tokens = this.getTokens();
    if (tokens && tokens.access) {
      // Check if token is expired
      if (this.isTokenExpired(tokens.access)) {
        // Token is expired, try to refresh it
        this.refreshToken().catch(() => {
          // If refresh fails, clear tokens (refresh token is also expired)
          this.clearTokens();
        });
      } else {
        // Check if token is about to expire
        const tokenData = this.parseJwt(tokens.access);
        const now = Date.now() / 1000;
        
        if (tokenData && tokenData.exp && tokenData.exp - now < this.refreshThreshold / 1000) {
          // Token is about to expire, refresh it
          this.refreshToken().catch(() => {
            // If refresh fails, clear tokens
            this.clearTokens();
          });
        }
      }
    }
  }

  parseJwt(token) {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      return null;
    }
  }

  async refreshToken() {
    // If login isn't required, never refresh
    if (!this.requireLoginForComparison) {
      return false;
    }
    const tokens = this.getTokens();
    if (!tokens || !tokens.refresh) {
      return false;
    }

    // Check if refresh token is expired
    if (this.isTokenExpired(tokens.refresh)) {
      console.log('Refresh token is expired, clearing tokens');
      this.clearTokens();
      this.currentUser = null;
      return false;
    }

    const runRefresh = async () => {
      try {
        // Create a request config that skips the interceptor's auth header
        // The refresh endpoint doesn't need Authorization header - it uses refresh token in body
        const response = await axios.post(`${this.baseURL}/api/token/refresh/`, {
          refresh: tokens.refresh
        }, {
          // Mark this request so interceptor knows not to add auth header
          skipAuthHeader: true
        });

        if (response.data.access) {
          this.setTokens({
            access: response.data.access,
            refresh: response.data.refresh || tokens.refresh
          });
          return true;
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        // If refresh token is expired, clear tokens
        const isRefreshExpired = error.response?.data?.code === 'token_not_valid' ||
          error.response?.data?.detail === 'Token is expired';
        if (isRefreshExpired) {
          this.clearTokens();
          this.currentUser = null;
        }
        return false;
      } finally {
        this.lastRefreshAttemptAt = Date.now();
        this.refreshInFlight = null;
      }
      return false;
    };

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const now = Date.now();
    const remaining = this.refreshCooldownMs - (now - this.lastRefreshAttemptAt);
    this.refreshInFlight = (async () => {
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      return runRefresh();
    })();

    return this.refreshInFlight;
  }

  // Check if user is authenticated
  async isAuthenticated() {
    try {
      const user = await this.getCurrentUser();
      return user !== null;
    } catch (error) {
      return false;
    }
  }

  // Get current user information
  async getCurrentUser() {
    // Skip user fetching entirely when login is not required
    if (!this.requireLoginForComparison) {
      console.log('Login not required, returning null user');
      return null;
    }
    if (this.currentUser) {
      return this.currentUser;
    }

    const tokens = this.getTokens();
    if (!tokens || !tokens.access) {
      console.log('No tokens found, returning null user');
      return null;
    }

    const profileUrl = `${this.baseURL}/api/user/profile/`;
    console.log('Fetching user profile from:', profileUrl);

    try {
      const response = await axios.get(profileUrl, {
        timeout: 10000
      });

      this.currentUser = response.data;
      console.log('User profile fetched successfully:', this.currentUser);
      return this.currentUser;
    } catch (error) {
      // Handle network errors, timeouts, and other errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('Request timeout when fetching user profile:', error);
        this.currentUser = null;
        return null;
      }
      
      if (error.response && error.response.status === 401) {
        // Check if this is a token expiration error
        const isTokenExpiredError = error.response?.data?.code === 'token_not_valid' ||
          error.response?.data?.messages?.some(msg => msg.message === 'Token is expired') ||
          error.response?.data?.detail === 'Token is expired';
        
        // If token is expired, try to refresh
        if (isTokenExpiredError) {
          try {
            const refreshed = await this.refreshToken();
            if (refreshed) {
              // Retry the request with new token
              const retryResponse = await axios.get(profileUrl, {
                timeout: 10000
              });
              this.currentUser = retryResponse.data;
              return this.currentUser;
            }
          } catch (refreshError) {
            console.error('Token refresh failed during getCurrentUser:', refreshError);
            // Clear tokens if refresh also failed
            this.clearTokens();
          }
        }
        
        // User not authenticated after refresh attempt
        this.currentUser = null;
        return null;
      }
      
      // Handle other HTTP errors (404, 500, etc.)
      if (error.response) {
        console.error('Error fetching user profile - HTTP', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('Error fetching user profile - No response received. Is the backend running?', error.request);
      } else {
        console.error('Error fetching user profile:', error.message);
      }
      
      this.currentUser = null;
      return null;
    }
  }

  // Clear cached user data (useful after payment to force refresh)
  clearUserCache() {
    this.currentUser = null;
    this.paymentConfig = null;
  }

  // Get payment configuration
  async getPaymentConfig() {
    if (this.paymentConfig) {
      return this.paymentConfig;
    }

    const configUrl = `${this.baseURL}/api/payment-config/`;
    console.log('Fetching payment config from:', configUrl);

    try {
      const response = await axios.get(configUrl, {
        timeout: 10000
      });
      this.paymentConfig = response.data;
      console.log('Payment config fetched successfully:', this.paymentConfig);
      
      // Sync runtime flag with backend setting if provided
      if (typeof response.data.require_login_for_comparison === 'boolean') {
        this.requireLoginForComparison = response.data.require_login_for_comparison;
        console.log('Updated requireLoginForComparison from backend:', this.requireLoginForComparison);
      }
      return this.paymentConfig;
    } catch (error) {
      // Handle network errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('Request timeout when fetching payment config:', error);
      } else if (error.response) {
        console.error('Error fetching payment config - HTTP', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('Error fetching payment config - No response received. Is the backend running?', error.request);
      } else {
        console.error('Error fetching payment config:', error.message);
      }
      
      // Default config when API fails
      this.paymentConfig = {
        payment_enabled: false,
        free_view_limit: 2,
        require_login_for_comparison: this.requireLoginForComparison
      };
      console.log('Using default payment config:', this.paymentConfig);
      return this.paymentConfig;
    }
  }

  // Check if user can select more institutions
  async canSelectMore(currentSelectionCount) {
    const config = await this.getPaymentConfig();

    if (!config.payment_enabled) {
      return { canSelect: true, reason: null };
    }

    const user = await this.getCurrentUser();
    const freeLimit = config.free_view_limit || 2;
    if (!user) {
      return {
        canSelect: false,
        reason: 'authentication_required',
        message: 'Opret en gratis konto for at sammenligne scorerne.',
      };
    }
    if (this.isPremiumActiveUser(user)) {
      return { canSelect: true, reason: null };
    }
    if (currentSelectionCount >= freeLimit) {
      return {
        canSelect: false,
        reason: 'limit_exceeded',
        message: `Basis kan sammenligne ${freeLimit} institutioner ad gangen.`,
        limit: freeLimit,
      };
    }
    return { canSelect: true, reason: null };
  }

  // Check if user can perform comparison (stricter check)
  async canCompare(selectedInstitutions) {
    const config = await this.getPaymentConfig();

    if (!config.payment_enabled) {
      return { canCompare: true, reason: null };
    }

    const user = await this.getCurrentUser();
    const freeLimit = config.free_view_limit || 2;
    if (!user) {
      return {
        canCompare: false,
        reason: 'authentication_required',
        message: 'Opret en gratis konto for at sammenligne scorerne.',
      };
    }

    if (this.isPremiumActiveUser(user)) {
      return { canCompare: true, reason: null };
    }

    if (selectedInstitutions.length > freeLimit) {
      return {
        canCompare: false,
        reason: 'limit_exceeded',
        message: `Basis kan sammenligne ${freeLimit} institutioner ad gangen. Du har valgt ${selectedInstitutions.length}.`,
        limit: freeLimit,
      };
    }

    return { canCompare: true, reason: null };
  }

  // Premium is time-limited; validate expiry when possible
  isPremiumActiveUser(user) {
    if (!user) return false;
    if (user.is_premium_active === true) return true;

    if (user.access_level !== 'premium') return false;

    // Backward compatibility: if backend hasn't shipped expiry yet, treat as premium
    if (!user.premium_expires_at) return true;

    const expiresAt = new Date(user.premium_expires_at);
    if (Number.isNaN(expiresAt.getTime())) return false;
    return expiresAt.getTime() > Date.now();
  }

  // Redirect to login
  // Redirect to login and return to the current in-app path (not the full URL).
  redirectToLogin() {
    const next = `${window.location.pathname}${window.location.search}`;
    const safeNext = next.startsWith('/') && !next.startsWith('//') && !next.includes('://')
      ? next
      : '/';
    window.location.href = `/login?next=${encodeURIComponent(safeNext)}`;
  }

  // Redirect to payment/upgrade
  redirectToUpgrade() {
    window.location.href = `${this.baseURL}/api/payment/`;
  }

  // Login method
  async login(username, password) {
    try {
      const response = await axios.post(`${this.baseURL}/api/token/`, {
        username,
        password
      }, {
        timeout: 10000
      });

      if (response.data.access) {
        // Store tokens
        this.setTokens({
          access: response.data.access,
          refresh: response.data.refresh
        });

        // Store user data
        this.currentUser = response.data.user;
        
        return { success: true, user: response.data.user };
      }

      return { success: false, error: 'Invalid credentials' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.response?.data?.error || 'Login failed' 
      };
    }
  }

  // Social login (Google / Apple): backend verifies the IdP token and returns
  // the same { access, refresh, user } shape as `login()`.
  async _socialLogin(provider, payload) {
    try {
      const gaIdentity = await captureGaIdentity();
      const body = { ...payload };
      if (gaIdentity.client_id) {
        body.ga_client_id = gaIdentity.client_id;
      }
      if (gaIdentity.session_id) {
        body.ga_session_id = gaIdentity.session_id;
      }
      const response = await axios.post(
        `${this.baseURL}/api/auth/${provider}/`,
        body,
        { timeout: 15000 }
      );

      if (response.data && response.data.access) {
        this.setTokens({
          access: response.data.access,
          refresh: response.data.refresh,
        });
        this.currentUser = response.data.user;
        return {
          success: true,
          user: response.data.user,
          isNewUser: response.status === 201,
        };
      }
      return { success: false, error: response.data?.message || 'Login failed.' };
    } catch (error) {
      const data = error.response?.data || {};
      if (error.response?.status === 409 && data.code === 'email_in_use') {
        return { success: false, code: 'email_in_use', error: data.message };
      }
      return {
        success: false,
        error: data.message || data.detail || data.error || 'Social login failed.',
      };
    }
  }

  async loginWithGoogle(idToken) {
    return this._socialLogin('google', { id_token: idToken });
  }

  async loginWithApple(idToken, name = null) {
    const payload = { id_token: idToken };
    if (name) {
      payload.name = name;
    }
    return this._socialLogin('apple', payload);
  }

  async listLinkedProviders() {
    try {
      const response = await axios.get(`${this.baseURL}/api/auth/social/`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      return { success: false, linked: [], has_usable_password: true };
    }
  }

  async linkProvider(provider, idToken) {
    try {
      const response = await axios.post(
        `${this.baseURL}/api/auth/social/link/`,
        { provider, id_token: idToken },
        { timeout: 15000 }
      );
      return { success: true, ...response.data };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        code: data.code,
        error: data.message || 'Kunne ikke tilknytte kontoen.',
      };
    }
  }

  async unlinkProvider(provider) {
    try {
      const response = await axios.post(
        `${this.baseURL}/api/auth/social/unlink/`,
        { provider },
        { timeout: 10000 }
      );
      return { success: true, ...response.data };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        code: data.code,
        error: data.message || 'Kunne ikke fjerne tilknytningen.',
      };
    }
  }

  // Logout method
  async logout() {
    try {
      const tokens = this.getTokens();
      if (tokens && tokens.refresh) {
        // Blacklist the refresh token
        await axios.post(`${this.baseURL}/api/logout/`, {
          refresh_token: tokens.refresh
        }, {
          timeout: 10000
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear cached data and tokens
    this.clearTokens();
    this.currentUser = null;
    this.paymentConfig = null;
    
    // Redirect to home page
    window.location.href = '/';
  }

  // Lock in institutions for free users when they first compare
  async lockInInstitutions(institutions) {
    try {
      const institutionIds = institutions.map(inst => inst.id);
      const response = await axios.post(`${this.baseURL}/api/user/lock-in-institutions/`, {
        institution_ids: institutionIds
      }, {
        timeout: 10000
      });
      
      if (response.data.success) {
        console.log('Institutions locked in successfully:', response.data.allowed_institutions);
        return response.data;
      } else {
        console.error('Failed to lock in institutions:', response.data.error);
        throw new Error(response.data.error);
      }
    } catch (error) {
      console.error('Error locking in institutions:', error);
      throw error;
    }
  }

  // Get user access level
  async getUserAccessLevel() {
    const user = await this.getCurrentUser();
    return user?.access_level || 'free';
  }

  // Create Stripe Checkout session for premium upgrade
  async createStripeCheckoutSession(redirectPath, gaClientId = null, extra = {}) {
    try {
      const payload = { redirect: redirectPath || window.location.pathname };
      if (gaClientId) {
        payload.ga_client_id = gaClientId;
      }
      if (extra && extra.ga_session_id) {
        payload.ga_session_id = extra.ga_session_id;
      }
      ['source', 'feature', 'institution_slug', 'placement', 'cta'].forEach((key) => {
        if (extra && extra[key]) {
          payload[key] = extra[key];
        }
      });
      const response = await axios.post(
        `${this.baseURL}/api/payment/create-checkout-session/`,
        payload,
        { timeout: 10000 }
      );
      if (response.data.session_id) {
        return response.data.session_id;
      } else {
        throw new Error(response.data.error || 'Failed to create Stripe session');
      }
    } catch (error) {
      console.error('Error creating Stripe Checkout session:', error);
      throw error;
    }
  }

  // Register method
  async register(username, email, firstName, lastName, password1, password2, gaClientId = null, gaSessionId = null) {
    try {
      const payload = {
        username,
        email,
        first_name: firstName,
        last_name: lastName,
        password1,
        password2,
      };
      if (gaClientId) {
        payload.ga_client_id = gaClientId;
      }
      if (gaSessionId) {
        payload.ga_session_id = gaSessionId;
      }
      const response = await axios.post(`${this.baseURL}/api/register/`, payload, {
        timeout: 10000
      });
      if (response.status === 201 && response.data.access) {
        // Store tokens for auto-login
        this.setTokens({
          access: response.data.access,
          refresh: response.data.refresh
        });

        // Store user data
        this.currentUser = response.data.user;
        
        return { success: true, user: response.data.user };
      }
      return { success: false, error: response.data?.error || 'Registration failed', fieldErrors: response.data?.errors || {} };
    } catch (error) {
      let fieldErrors = {};
      let errorMsg = 'Registration failed';
      if (error.response?.data?.errors) {
        fieldErrors = error.response.data.errors;
        errorMsg = Object.values(fieldErrors).flat().join(' ');
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      }
      return { success: false, error: errorMsg, fieldErrors };
    }
  }

  // Request password reset
  async requestPasswordReset(email) {
    try {
      const response = await axios.post(`${this.baseURL}/api/password-reset/`, {
        email
      }, {
        timeout: 10000
      });
      return { 
        success: true, 
        message: response.data?.message || 'Hvis denne email findes i systemet, vil du modtage en email med instruktioner.'
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kunne ikke sende nulstillingsemail. Prøv venligst igen.'
      };
    }
  }

  // Confirm password reset
  async confirmPasswordReset(uid, token, newPassword) {
    try {
      const response = await axios.post(`${this.baseURL}/api/password-reset/confirm/`, {
        uid,
        token,
        new_password: newPassword
      }, {
        timeout: 10000
      });
      
      if (response.data.access && response.data.refresh) {
        // Store tokens for auto-login
        this.setTokens({
          access: response.data.access,
          refresh: response.data.refresh
        });
        
        // Store user data
        this.currentUser = response.data.user;
        
        return { 
          success: true, 
          message: response.data?.message || 'Adgangskode nulstillet succesfuldt',
          user: response.data.user
        };
      }
      
      return { success: false, error: 'Kunne ikke nulstille adgangskode' };
    } catch (error) {
      let errorMsg = 'Kunne ikke nulstille adgangskode';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
        if (error.response.data.details) {
          errorMsg += ': ' + error.response.data.details.join(', ');
        }
      }
      return { success: false, error: errorMsg };
    }
  }
}

const instance = new AuthService();
export { instance as AuthService, AuthService as AuthServiceClass };
